import { UserRoleOption } from "../types/user.types";

export const SUPPLIER_ROLE = "Supplier";

/**
 * How the role field behaves for the signed-in user. Each implementation owns
 * one rule-set (open/closed): adding a new rule means adding a class, not
 * editing the forms or view-models that consume the contract.
 */
export interface RoleSelectionPolicy {
  /** When true the role cannot be changed in the form. */
  readonly isLocked: boolean;
  /** Role pre-selected in a blank form. */
  readonly defaultRole: string;
  /** Roles the form offers. */
  visibleRoles(available: UserRoleOption[]): UserRoleOption[];
  /** Role value as the API expects it in a create payload. */
  toRegisterPayload(selected: string): string | string[];
}

/** Regular admins/users: pick any role; the API takes a one-item array. */
export class StandardRolePolicy implements RoleSelectionPolicy {
  readonly isLocked: boolean = false;
  readonly defaultRole: string = "";

  visibleRoles(available: UserRoleOption[]): UserRoleOption[] {
    return available;
  }

  toRegisterPayload(selected: string): string[] {
    return selected ? [selected] : [];
  }
}

/** Edit: the role is fixed once the account exists, so it is shown but cannot be changed. */
export class LockedAfterRegistrationPolicy extends StandardRolePolicy {
  readonly isLocked = true;
}

/** Administrators may change a role after registration; everyone else sees it read-only. */
export const ROLE_EDITOR_ROLES = ["Root Admin", "Group Admin", "Admin"];

export const createEditRolePolicy = (currentUserRole?: string): RoleSelectionPolicy =>
  currentUserRole && ROLE_EDITOR_ROLES.includes(currentUserRole)
    ? new StandardRolePolicy()
    : new LockedAfterRegistrationPolicy();

/** A supplier-role user can only ever register further suppliers (legacy behaviour: plain string payload). */
export class SupplierRolePolicy implements RoleSelectionPolicy {
  readonly isLocked = true;
  readonly defaultRole = SUPPLIER_ROLE;

  visibleRoles(available: UserRoleOption[]): UserRoleOption[] {
    const supplier = available.find(role => role.text === SUPPLIER_ROLE);
    return [supplier ?? { key: SUPPLIER_ROLE, text: SUPPLIER_ROLE, value: SUPPLIER_ROLE }];
  }

  toRegisterPayload(): string {
    return SUPPLIER_ROLE;
  }
}

export const createRoleSelectionPolicy = (currentUserRole?: string): RoleSelectionPolicy =>
  currentUserRole === SUPPLIER_ROLE ? new SupplierRolePolicy() : new StandardRolePolicy();
